import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/shared/api/client';
import { useOperator } from '@/shared/auth/OperatorContext';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Button } from '@/shared/ui/button';
import Breadcrumb from '@/shared/components/Breadcrumb';

// Les montants du bloc de remuneration sont lus cote a cote : sans separateur de
// milliers ni decimales stables, deux nombres voisins ne se comparent pas a l'oeil.
const euros = (v, decimales = 0) => `${Number(v || 0).toLocaleString('fr-FR', {
  minimumFractionDigits: decimales, maximumFractionDigits: decimales })} €`;
import {
  Flame, Sun, Droplets, Wifi, WifiOff, AlertTriangle, Clock,
  ChevronRight, Zap, Users, BatteryCharging, TrendingUp
} from 'lucide-react';

// LES ICONES DU TABLEAU DE BORD VIENNENT DU MENU, jamais d'ici (item M2).
//
// David a demande que les cartes portent LES MEMES icones que les entrees de menu :
// c'est le lien visuel entre les deux qu'il veut, pas des icones en plus. On lit donc
// la table que la barre laterale derive de ses propres entrees, plutot que d'importer
// `Users`, `Map`, `Activity` et `Bell` une seconde fois — deux jeux pour les memes
// ecrans auraient diverge au premier ecran ajoute au menu.
//
// Le tableau de bord importait deja `MapPin` et `Bell` pour ses en-tetes : `Bell`
// tombait juste par hasard, et `MapPin` n'etait meme PAS l'icone de l'entree
// « Carte des actifs », qui porte `Map`. La divergence avait donc deja commence.
import { ICONE_PAR_ROUTE } from '@/shared/components/Sidebar';

import { ICON as TYPE_ICON, LABEL as TYPE_LABEL, COLOR as TYPE_COLOR } from '@/shared/constants/assetTypes';
import PowerSummaryPanel from '@/features/assets/components/PowerSummaryPanel';
import ProductionSummaryPanel from '@/features/assets/components/ProductionSummaryPanel';
import AssetsStatusPanel from '@/features/assets/components/AssetsStatusPanel';
import MentionEstimation from '@/shared/components/MentionEstimation';

const MARKER_FILL = { PAC: '#f97316', PV: '#f59e0b', Ballon: '#06b6d4', Batterie: '#8b5cf6' };
const CONN_BORDER = { connecte: '#22c55e', deconnecte: '#ef4444', inconnu: '#a3a3a3' };

function createIcon(type, status) {
  const fill = MARKER_FILL[type] || '#a3a3a3';
  const border = CONN_BORDER[status] || '#a3a3a3';
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="width:14px;height:14px;border-radius:50%;background:${fill};border:2.5px solid ${border};box-shadow:0 1px 3px rgba(0,0,0,0.15);"></div>`,
    iconSize: [14, 14], iconAnchor: [7, 7],
  });
}

export default function DashboardPage() {
  const { operator } = useOperator();
  // Le contexte operateur etait servi par /api/operator/me et lu par personne. Le
  // repli suit l'ordre de precision : l'enseigne reglee, puis le nom du sous-compte.
  const enseigne = operator?.branding?.raison_sociale || operator?.name || '';
  const [stats, setStats] = useState(null);
  const [installations, setInstallations] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [revenus, setRevenus] = useState(null);
  const [puissance, setPuissance] = useState(null);
  const [etatsActifs, setEtatsActifs] = useState(null);
  const [production, setProduction] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersLayer = useRef(null);

  useEffect(() => {
    Promise.all([
      api.get('/dashboard/stats'),
      api.get('/installations'),
      api.get('/alerts'),
      // Sa version lit la quote-part operateur (DashboardPage.js:54), et c'est le
      // chiffre qui correspond aux ecrans d estimation. `/admin/revenus` passe par un
      // troisieme moteur de calcul, par ACTIF et non par client : il annoncait
      // 47 455 € la ou les ecrans Symphonics et Survoltage totalisent 9 731 €.
      api.get('/dashboard/operator-revenue-summary').catch(() => ({ data: null })),
      // Deux routes servies depuis le portage, que plus aucun ecran ne lisait :
      // la puissance du parc et l'etat de connexion equipement par equipement.
      api.get('/dashboard/power-summary').catch(() => ({ data: null })),
      api.get('/dashboard/assets-status').catch(() => ({ data: null })),
      // Ce que le parc PRODUIT, par opposition a ce qu il a ete declare capable de
      // produire. La production remontait jusqu au serveur et s arretait la : aucun
      // ecran ne la demandait, et le tableau de bord sous-estimait le parc en
      // silence. Constate par David le 30/08 sur sa centrale Hoymiles.
      api.get('/dashboard/production-summary').catch(() => ({ data: null })),
    ]).then(([statsR, instR, alertsR, revR, powerR, statusR, prodR]) => {
      setStats(statsR.data);
      setInstallations(instR.data);
      setAlerts(alertsR.data);
      setRevenus(revR.data);
      setPuissance(powerR.data);
      setEtatsActifs(statusR.data);
      setProduction(prodR.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  // Init map
  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return;
    mapInstance.current = L.map(mapRef.current, {
      zoomControl: false, attributionControl: false,
    }).setView([46.8, 2.2], 5.5);
    L.tileLayer('https://{s}.tile.openstreetmap.fr/osmfr/{z}/{x}/{y}.png', { maxZoom: 19, attribution: '© OpenStreetMap France' }).addTo(mapInstance.current);
    markersLayer.current = L.layerGroup().addTo(mapInstance.current);
    // Fix tile rendering after container resize
    setTimeout(() => { mapInstance.current?.invalidateSize(); }, 300);
    return () => { mapInstance.current?.remove(); mapInstance.current = null; };
  }, [loading]);

  // Populate markers (interactifs : clic ouvre fiche client)
  useEffect(() => {
    if (!markersLayer.current) return;
    markersLayer.current.clearLayers();
    installations.forEach(inst => {
      if (!inst.latitude || !inst.longitude) return;
      const marker = L.marker([inst.latitude, inst.longitude], {
        icon: createIcon(inst.asset_type, inst.statut_connexion),
      });
      if (inst.client_id) {
        marker.bindTooltip(
          `<div style="font-family:Satoshi,sans-serif;font-size:11px;line-height:1.3">
            <strong>${inst.client_nom || ''}</strong><br/>
            ${inst.asset_type} — ${inst.client_ville || ''}<br/>
            <em style="color:#64748B">Cliquer pour ouvrir la fiche</em>
          </div>`,
          { direction: 'top', offset: [0, -8] }
        );
        marker.on('click', () => navigate(`/clients/${inst.client_id}`));
      }
      marker.addTo(markersLayer.current);
    });
  }, [installations, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-6 h-6 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" />
      </div>
    );
  }
  if (!stats) return null;

  const totalConn = installations.filter(i => i.statut_connexion === 'connecte').length;
  const totalDisc = installations.filter(i => i.statut_connexion === 'deconnecte').length;

  return (
    <div className="p-6 md:p-8 lg:p-10 space-y-6" data-testid="dashboard-page">
      <div>
        <Breadcrumb items={[{ label: 'Tableau de bord' }]} />
        <h1 className="text-[26px] font-semibold tracking-tight text-neutral-900">Tableau de bord</h1>
        {/* L'enseigne de l'operateur, jamais celle de l'editeur : ce tableau de
            bord s'affiche chez le client de notre client. Sans enseigne reglee, la
            phrase se termine apres « parc » plutot que de nommer qui que ce soit. */}
        <p className="text-[16px] text-neutral-400 mt-0.5" data-testid="dashboard-sous-titre">
          {enseigne ? `Vue globale du parc ${enseigne}` : 'Vue globale du parc'}
        </p>
      </div>

      {/* KPIs.

          CHAQUE CARTE DECLARE L'ECRAN QU'ELLE RESUME, et c'est de la que vient son
          icone (item M2). Le rapprochement n'est pas cosmetique : « Actifs » resume ce
          que « Carte des actifs » montre, « Connectes » ce que Monitoring surveille,
          « Deconnectes » ce que les Alertes signalent — et cette derniere carte y mene
          deja par un clic. « Mandats actifs » se rattache aux gabarits de mandat, seule
          entree de menu qui parle de mandats. */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <KPI label="Clients" route="/clients" value={stats.total_clients} sub={`${stats.clients_by_status?.actif || 0} actifs`} />
        <KPI label="Mandats actifs" route="/gabarits-mandat" value={stats.active_mandats} />
        <KPI label="Actifs" route="/carte" value={stats.total_assets} sub={`${totalConn} connectés`} />
        <KPI label="Connectés" route="/monitoring" value={totalConn} accent="text-emerald-600" />
        <button
          type="button"
          onClick={() => navigate('/alertes')}
          className={`text-left p-4 rounded-lg border bg-white transition-all hover:shadow-sm ${
            totalDisc > 0 ? 'border-red-200 hover:border-red-300' : 'border-neutral-200/80'
          }`}
          data-testid="kpi-deconnectes-card"
        >
          <p className="text-[12px] uppercase tracking-wider text-neutral-400 font-medium flex items-center gap-1.5">
            <IconeEcran route="/alertes" />Déconnectés
          </p>
          <p className={`text-[26px] font-semibold mt-0.5 tracking-tight ${totalDisc > 0 ? 'text-red-600' : 'text-neutral-900'}`} data-testid="kpi-deconnectes-value">
            {totalDisc}
          </p>
          <p className={`text-[12px] mt-0.5 flex items-center gap-0.5 ${totalDisc > 0 ? 'text-red-500 font-medium' : 'text-neutral-400'}`}>
            Voir les alertes <ChevronRight className="w-3 h-3" />
          </p>
        </button>
      </div>

      {/* Part operateur estimee.

          Ce bloc repond a la seule question que l'operateur pose devant cet ecran :
          combien je garde, sur combien, et combien part chez qui. Nos trois cartes
          plates donnaient le premier chiffre et cachaient les deux autres, alors que
          la route les servait deja. La part negociee est affichee par agregateur
          parce qu'elle se negocie separement avec chacun. */}
      {revenus && (
        <div className="rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50/60 via-white to-white p-5 md:p-6" data-testid="operator-revenue-block">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-9 h-9 rounded-md bg-emerald-100 flex items-center justify-center shrink-0">
              <TrendingUp className="w-4 h-4 text-emerald-700" />
            </div>
            <div>
              <h2 className="text-[16px] font-semibold text-neutral-900">Part opérateur estimée</h2>
              <p className="text-[13px] text-neutral-500">Quote-part conservée après partage avec les agrégateurs</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-1 p-5 rounded-lg bg-emerald-600 text-white" data-testid="db-quote-part">
              <p className="text-[12px] uppercase tracking-widest opacity-80 font-medium">Quote-part opérateur estimée</p>
              <p className="text-[32px] lg:text-[38px] font-bold tracking-tight mt-2" data-testid="db-quote-part-annuelle">
                {euros(revenus.quote_part_annuel)}<span className="text-[16px] font-normal opacity-70"> /an</span>
              </p>
              <p className="text-[16px] opacity-90 mt-1" data-testid="db-quote-part-mensuelle">
                {euros(revenus.quote_part_mensuel, 2)} <span className="opacity-70">/mois</span>
              </p>
              <p className="text-[13px] opacity-80 mt-3 pt-3 border-t border-white/20">
                Soit <strong>{revenus.part_operateur_pct ?? 0}%</strong> du total brut estimé
              </p>
            </div>

            <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3 content-start">
              <div className="p-4 rounded-lg border border-neutral-200 bg-white" data-testid="db-brut-annuel">
                <p className="text-[12px] uppercase tracking-wider text-neutral-400 font-medium">Estimation annuelle brute</p>
                <p className="text-[26px] font-semibold text-neutral-900 mt-1">{euros(revenus.brut_annuel)}</p>
                <p className="text-[12.5px] text-neutral-400 mt-0.5">Avant partage agrégateurs</p>
              </div>
              <div className="p-4 rounded-lg border border-neutral-200 bg-neutral-50/60" data-testid="db-part-agregateurs">
                <p className="text-[12px] uppercase tracking-wider text-neutral-400 font-medium">Part agrégateurs estimée</p>
                <p className="text-[26px] font-semibold text-neutral-500 mt-1">{euros(revenus.part_agregateurs)}</p>
                {/* « aux agrégateurs », sans les nommer : même retour de David (24/09) que
                    la réserve du parc, voir `MentionEstimation`. Le détail par agrégateur
                    suit juste en dessous, c'est lui qui les nomme. */}
                <p className="text-[12.5px] text-neutral-400 mt-0.5">Reversée aux agrégateurs</p>
              </div>
              {Object.entries(revenus.par_agregateur || {}).map(([agg, noeud]) => (
                <div key={agg} className="sm:col-span-2 p-3 rounded-md bg-white border border-neutral-200/70 flex items-center justify-between gap-3" data-testid={`db-agg-${agg.toLowerCase()}`}>
                  <div className="min-w-0">
                    <p className="text-[14px] font-medium text-neutral-800">{agg}</p>
                    <p className="text-[12px] text-neutral-500">Part opérateur négociée : <strong>{noeud.part_operateur_pct ?? 0}%</strong></p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-[13px] text-neutral-500">Brut estimé : {euros(noeud.brut_annuel)}</p>
                    <p className="text-[16px] font-semibold text-emerald-700">{euros(noeud.quote_part_annuel)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <MentionEstimation source="parc" bloc="tableau-de-bord" className="mt-4" />
        </div>
      )}

      {puissance && <PowerSummaryPanel data={puissance} />}
      {production && <ProductionSummaryPanel data={production} />}

      {/* Map + Alerts grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Map */}
        <div className="lg:col-span-2 rounded-lg border border-neutral-200/80 bg-white overflow-hidden" data-testid="dashboard-map">
          <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-100">
            <div className="flex items-center gap-2">
              <IconeEcran route="/carte" />
              <span className="text-[15px] font-medium text-neutral-900">Carte du parc</span>
              <span className="text-[13px] text-neutral-400">{installations.length} actifs</span>
            </div>
            <div className="flex items-center gap-3 text-[12px] text-neutral-400">
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-orange-500 inline-block" />PAC</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-amber-500 inline-block" />PV</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-cyan-500 inline-block" />ECS</span>
              <span className="text-neutral-200">|</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full border-2 border-emerald-500 inline-block" />OK</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full border-2 border-red-500 inline-block" />HS</span>
            </div>
            <Button variant="ghost" size="sm" className="text-[14px]" onClick={() => navigate('/carte')} data-testid="open-full-map-btn">
              Voir la carte <ChevronRight className="w-3 h-3 ml-0.5" />
            </Button>
          </div>
          <div ref={mapRef} style={{ height: '340px' }} />
        </div>

        {/* Alerts */}
        <div className="rounded-lg border border-neutral-200/80 bg-white overflow-hidden flex flex-col" data-testid="dashboard-alerts">
          <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-100">
            <div className="flex items-center gap-2">
              <IconeEcran route="/alertes" />
              <span className="text-[15px] font-medium text-neutral-900">Alertes actives</span>
              {alerts.length > 0 && (
                <span className="min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[12px] flex items-center justify-center font-semibold px-1">
                  {alerts.length}
                </span>
              )}
            </div>
            <Button variant="ghost" size="sm" className="text-[14px]" onClick={() => navigate('/alertes')} data-testid="open-alerts-btn">
              Tout voir <ChevronRight className="w-3 h-3 ml-0.5" />
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {alerts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-neutral-300">
                <IconeEcran route="/alertes" className="w-6 h-6 mb-2" />
                <p className="text-[14px]">Aucune alerte</p>
              </div>
            ) : alerts.slice(0, 8).map(alert => (
              <button
                key={alert.id}
                onClick={() => navigate(`/clients/${alert.client_id}`)}
                className="w-full flex items-start gap-2.5 px-4 py-3 border-b border-neutral-50 last:border-0 hover:bg-neutral-50/50 transition-colors text-left"
                data-testid={`dash-alert-${alert.id}`}
              >
                {alert.type === 'deconnexion' ? (
                  <WifiOff className="w-3.5 h-3.5 text-red-500 mt-0.5 flex-shrink-0" />
                ) : (
                  <Clock className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-[14px] text-neutral-800 leading-tight truncate">{alert.message}</p>
                  <p className="text-[12px] text-neutral-400 mt-0.5">{alert.client_nom}</p>
                </div>
                <span className={`text-[12px] font-medium px-1.5 py-0.5 rounded ${
                  alert.priority === 'critique' ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-600'
                }`}>
                  {alert.priority === 'critique' ? 'Critique' : 'Alerte'}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {etatsActifs && (
        <AssetsStatusPanel data={etatsActifs} onOuvrirClient={(id) => navigate(`/clients/${id}`)} />
      )}

      {/* Asset breakdown */}
      <div>
        <h2 className="text-[15px] font-medium text-neutral-900 mb-3">Parc d'actifs par type</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {Object.entries(stats.assets_by_type || {}).map(([type, data]) => {
            const Icon = TYPE_ICON[type] || Droplets;
            const colorCls = TYPE_COLOR[type] || 'text-neutral-400';
            const total = data.total || 1;
            const connPct = Math.round((data.connecte / total) * 100);
            return (
              <div key={type} className="p-4 rounded-lg border border-neutral-200/80 bg-white">
                <div className="flex items-center gap-2.5 mb-3">
                  <div className={`w-7 h-7 rounded-md bg-neutral-50 flex items-center justify-center ${colorCls}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1">
                    <span className="text-[16px] font-medium text-neutral-900">{TYPE_LABEL[type] || type}</span>
                    <span className="text-[13px] text-neutral-400 ml-2">{data.total} total</span>
                  </div>
                  <span className="text-[22px] font-semibold text-neutral-900">{data.actif}</span>
                  <span className="text-[12px] text-neutral-400">sous mandat</span>
                </div>
                {/* Connection bar */}
                <div className="h-1.5 rounded-full bg-neutral-100 overflow-hidden mb-2">
                  <div className="h-full rounded-full bg-emerald-400" style={{ width: `${connPct}%` }} />
                </div>
                <div className="flex justify-between text-[12px] text-neutral-400">
                  <span className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />{data.connecte} connectés
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-400" />{data.deconnecte} deconnectés
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Pipeline */}
      <div>
        <h2 className="text-[15px] font-medium text-neutral-900 mb-3">Pipeline clients</h2>
        <div className="flex flex-wrap gap-3">
          {[
            { key: 'prospect', label: 'Prospects', color: 'bg-neutral-100 text-neutral-600' },
            { key: 'mandat_envoye', label: 'Mandat envoye', color: 'bg-amber-50 text-amber-700' },
            { key: 'signe', label: 'Signes', color: 'bg-blue-50 text-blue-700' },
            { key: 'actif', label: 'Actifs', color: 'bg-emerald-50 text-emerald-700' },
            { key: 'resilie', label: 'Resilies', color: 'bg-red-50 text-red-600' },
          ].map(({ key, label, color }) => (
            <button
              key={key}
              onClick={() => navigate(`/clients?statut=${key}`)}
              className="flex items-center gap-2.5 px-4 py-2.5 rounded-lg border border-neutral-200/80 bg-white hover:border-neutral-300 transition-colors cursor-pointer"
              data-testid={`pipeline-${key}`}
            >
              <span className={`px-2 py-0.5 rounded-md text-[13px] font-medium ${color}`}>{label}</span>
              <span className="text-[20px] font-semibold text-neutral-900">{stats.clients_by_status?.[key] || 0}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/** L'icone de l'entree de menu qui mene a `route`, ou rien du tout.
 *
 * ⚠️ Le repli silencieux est VOULU. Une route absente du menu — parce qu'on l'a
 * renommee, ou parce que l'entree n'existe pas — rend `undefined`, et React affiche
 * alors une carte sans icone plutot que de tomber. Ce n'est PAS une excuse pour se
 * passer de controle : `test_lot_m_habillage.py` compare les routes lues ici aux
 * routes que le menu declare, et une orpheline devient un rouge de CI. Le repli evite
 * l'ecran blanc, le test evite l'icone qui disparait sans que personne le voie.
 */
function IconeEcran({ route, className = 'w-4 h-4 text-neutral-400 shrink-0' }) {
  const Icone = ICONE_PAR_ROUTE[route];
  return Icone ? <Icone className={className} aria-hidden /> : null;
}

function KPI({ label, value, sub, accent, route }) {
  return (
    <div className="p-4 rounded-lg border border-neutral-200/80 bg-white">
      <p className="text-[12px] uppercase tracking-wider text-neutral-400 font-medium flex items-center gap-1.5">
        <IconeEcran route={route} />{label}
      </p>
      <p className={`text-[26px] font-semibold mt-0.5 tracking-tight ${accent || 'text-neutral-900'}`} data-testid={`kpi-${label.replace(/\s/g, '-').toLowerCase()}`}>
        {value}
      </p>
      {sub && <p className="text-[12px] text-neutral-400 mt-0.5">{sub}</p>}
    </div>
  );
}
